import { ProductType, type Country } from '../constants';
import type {
  CartResponseResponse,
  CartRewardProduct,
  CartRewardGroup,
  ProgressBarResponse,
  SimpleProduct,
  VariationProduct,
} from '../model';

// Remove binary subtraction noise without rounding to one currency's display precision.
const REMAINDER_PRECISION_SCALE = 1e8;

/** Purchasability checks only; allocates nothing, so it suits `filter`/`some`. */
export function isCartRewardProductEligible({ product, projection }: CartRewardProduct): boolean {
  if (!projection.sku.trim()) {
    return false;
  }
  if (projection.quantity !== 1) {
    return false;
  }
  if (!Number.isFinite(projection.price) || projection.price < 0) {
    return false;
  }
  if (
    projection.priceAfterReward !== null &&
    (!Number.isFinite(projection.priceAfterReward) || projection.priceAfterReward < 0)
  ) {
    return false;
  }
  return product.status === 'instock';
}

/** Once shipping is free, lead with the nearest server-provided discount tier. */
export function getCartBannerRewardGroup(groups: CartRewardGroup[], hasFreeShipping = false) {
  const eligible = groups
    .map((group) => ({
      ...group,
      products: group.products.filter(isCartRewardProductEligible),
    }))
    .filter((group) => group.products.length > 0);
  const almostThere = eligible.find((group) => group.type === 'almost_there');
  const discountGroup = eligible
    .filter(
      (group): group is Extract<CartRewardGroup, { type: 'unlocks' }> =>
        group.type === 'unlocks' && group.reward.type === 'discount_tier',
    )
    .sort((a, b) => a.reward.minAmount - b.reward.minAmount)[0];
  if (hasFreeShipping && discountGroup) {
    return discountGroup;
  }
  return almostThere ?? discountGroup;
}

/** UI priority only; preserve server order within each group category. */
export function prioritizeAlmostThereGroups(groups: CartRewardGroup[]): CartRewardGroup[] {
  return [
    ...groups.filter((group) => group.type === 'almost_there'),
    ...groups.filter((group) => group.type !== 'almost_there'),
  ];
}

/** Display-only remainder after one Almost There product; discounts remain server-owned. */
export function withCartRewardRemaining(
  groups: CartRewardGroup[],
  progressBar?: ProgressBarResponse | null,
): CartRewardGroup[] {
  const tiers = progressBar?.discountTiers ?? [];
  const appliedThreshold = Math.max(0, ...tiers.filter((tier) => tier.applied).map((tier) => tier.minAmount));
  const remaining = tiers
    .filter((tier) => {
      const isUpcomingTier = !tier.applied && tier.minAmount > appliedThreshold;
      const hasRemainingAmount = Number.isFinite(tier.remaining) && tier.remaining > 0;
      return isUpcomingTier && hasRemainingAmount;
    })
    .reduce((nearest, tier) => Math.min(nearest, tier.remaining), Infinity);
  return groups.map((group) => {
    if (group.type !== 'almost_there') {
      return group;
    }
    return {
      ...group,
      products: group.products.map((entry) => {
        const price = entry.projection.price;
        const canCalculateRemainder = Number.isFinite(remaining) && Number.isFinite(price) && price >= 0;
        const amount = canCalculateRemainder
          ? Math.max(0, Math.round((remaining - price) * REMAINDER_PRECISION_SCALE) / REMAINDER_PRECISION_SCALE)
          : undefined;
        return { ...entry, projection: { ...entry.projection, remainingAfterAdd: amount } };
      }),
    };
  });
}

/** Local comparison key only. Contains no session key, token, or authentication credential. */
export function getCartDiscoverySnapshotKey({
  cart,
  country,
  actorId,
  isAuthenticated,
  customerId,
}: {
  cart: CartResponseResponse['cart'];
  country?: Country;
  actorId?: string;
  isAuthenticated: boolean;
  customerId: string | number | null;
}): string {
  return JSON.stringify({
    country,
    actorId,
    isAuthenticated,
    customerId,
    currency: cart.currency.currency,
    items: cart.item
      .map(({ key, sku, quantity, price }) => ({ key, sku, quantity, price }))
      .sort((a, b) => a.key.localeCompare(b.key)),
    totals: cart.totals,
    coupons: cart.coupons,
    shippingRates: cart.shippingRates,
    progressBar: cart.progressBar,
  });
}

/** Honor the supplied variant exactly; never substitute a different cheapest SKU. */
export function getCartRewardProductTarget(offer: CartRewardProduct): SimpleProduct | VariationProduct | null {
  if (!isCartRewardProductEligible(offer)) {
    return null;
  }
  const { product, projection } = offer;
  // The endpoint resolves the purchasable variation. Catalog payloads need not
  // contain its siblings, and choosing a cheaper sibling would invalidate the quote.
  return {
    ...product,
    type: product.type === ProductType.Variable ? ProductType.Variation : ProductType.Simple,
    sku: projection.sku,
    originalPrice: projection.price,
    salePrice: undefined,
  };
}

/**
 * Strike-through price for a reward offer, or null when there is nothing to compare against.
 * A reward reduction compares to the selling price; otherwise the catalog's own original price
 * applies only when it is higher than what is being charged.
 */
export function getCartRewardCompareAtPrice({ product, projection }: CartRewardProduct): number | null {
  const price = projection.priceAfterReward ?? projection.price;
  if (price < projection.price) {
    return projection.price;
  }
  if (projection.priceAfterReward !== null) {
    return null;
  }
  const catalogProduct =
    'originalPrice' in product ? product : product.variations?.find(({ sku }) => sku === projection.sku);
  if (catalogProduct && Number.isFinite(catalogProduct.originalPrice) && catalogProduct.originalPrice > price) {
    return catalogProduct.originalPrice;
  }
  return null;
}
